import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { fetchAdmins, deleteData } from "../../services/apiServices";
import { Spinner, Button } from "reactstrap";
import { toast } from "react-toastify";
import { getUser } from "../../helpers/auth-helpers";
import { useNavigate } from "react-router-dom";

const AdminList = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = getUser();
    if (user) setCurrentUser(user);
  }, []);

  const getAdmins = async () => {
    try {
      setPending(true);
      const response = await fetchAdmins();

      if (Array.isArray(response.data)) {
        setRows(response.data);
      } else {
        setError("Invalid API response structure");
      }
    } catch (err) {
      setError("Failed to fetch admins");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    getAdmins();
  }, []);

  const handleDelete = async (id) => {
    if (currentUser && id === currentUser._id) {
      toast.error("You cannot delete your own account");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this admin?")) return;

    try {
      await deleteData(`api/v2/admin/auth/admin/${id}`);
      toast.success("Admin deleted successfully");
      getAdmins();
    } catch (error) {
      toast.error("Failed to delete admin");
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Role",
      selector: (row) => row.role,
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`badge ${row.isActive ? "bg-success" : "bg-danger"}`}>
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            color="info"
            onClick={() => navigate(`/admins/view/${row._id}`)}
            title="View">
            <i className="ri-eye-line" />
          </Button>

          <Button
            size="sm"
            color="warning"
            onClick={() => navigate(`/admins/edit/${row._id}`)}
            title="Edit">
            <i className="ri-edit-line" />
          </Button>

          <Button
            size="sm"
            color="secondary"
            onClick={() => navigate(`/admins/password/${row._id}`)}
            title="Update Password">
            <i className="ri-lock-password-line" />
          </Button>

          <Button
            size="sm"
            color="danger"
            onClick={() => handleDelete(row._id)}
            disabled={currentUser && row._id === currentUser._id}
            title="Delete">
            <i className="ri-delete-bin-line" />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <DataTable
      columns={columns}
      data={rows}
      pagination
      progressPending={pending}
      progressComponent={<Spinner />}
      highlightOnHover
      responsive
    />
  );
};

export default AdminList;
